import { useTranslation } from '../../../../../i18n/hooks/useTranslation';
import { toBnDigits } from '../../../../../utils/bnFormat';
import { auditGrade } from './qrAudit';

const GRADE_STYLES = {
    great: 'bg-[#E3F6EC] text-[#14713F] dark:bg-emerald-950/30 dark:text-emerald-400',
    okay: 'bg-[#FFF1D9] text-[#8A6208] dark:bg-amber-950/30 dark:text-amber-400',
    risky: 'bg-[#FDE8E4] text-[#B2291A] dark:bg-red-950/30 dark:text-red-400',
};

const ISSUE_STYLES = {
    ok: 'bg-[#E3F6EC] text-[#14713F] dark:bg-emerald-950/25 dark:text-emerald-400',
    warn: 'bg-[#FFF7EA] text-[#8A6208] dark:bg-amber-950/25 dark:text-amber-400',
    bad: 'bg-[#FDE8E4] text-[#B2291A] dark:bg-red-950/25 dark:text-red-400',
};

const ISSUE_MARKS = { ok: '✓', warn: '•', bad: '!' };

function barColor(score) {
    if (score >= 85) return '#1E9E5E';
    if (score >= 65) return '#C98A11';
    return '#D33A2C';
}

/** Score card: how likely a phone is to read this code, and what to fix. */
export function QrScoreCard({ audit, verifying, verification, ready }) {
    const { t } = useTranslation();
    const grade = auditGrade(audit.score);

    // Nothing encoded yet: scoring the colours of a code that does not exist
    // would just read as a confident 100.
    if (!ready) {
        return (
            <div className="rounded-[20px] border border-[#ECE8E4] bg-white p-[18px] shadow-[0_1px_2px_rgba(28,24,21,0.04)] dark:border-zinc-700 dark:bg-zinc-900">
                <div className="mb-3 text-[14px] font-bold text-[#1C1815] dark:text-zinc-100">{t('qrCode.scoreTitle')}</div>
                {/* A placeholder block instead of a dash: it sits on the same
                    optical line as "/ ১০০", which a glyph does not. */}
                <div className="mb-3 flex items-center gap-2">
                    <span className="h-[26px] w-[54px] rounded-lg bg-[#F2EFEC] dark:bg-zinc-800" />
                    <span className="text-[12.5px] leading-none text-[#6B635B]">/ {toBnDigits(100)}</span>
                </div>
                <div className="mb-3 h-2 rounded-md bg-[#F2EFEC] dark:bg-zinc-800" />
                <p className="text-[12.5px] text-[#6B635B] dark:text-zinc-400">{t('qrCode.scoreEmpty')}</p>
            </div>
        );
    }

    // One line covers the common case: the reader read it and nothing is wrong.
    // Anything else keeps its own row, since each names a different fix.
    const verified = verification?.status === 'readable';
    const clean = audit.issues.every((issue) => issue.level === 'ok');
    const failed = audit.issues.find((issue) => issue.key.startsWith('decode'));
    const rows = verifying
        ? [{ level: 'ok', key: 'decode.checking', busy: true }]
        : verified && clean
            ? [{ level: 'ok', key: 'audit.verifiedGood' }]
            // A failed decode is the verdict, and its line already names the
            // fixes — the individual warnings would only repeat them.
            : failed
                ? [{ level: 'bad', key: `audit.${failed.key}` }]
                : audit.issues.map((issue) => ({ level: issue.level, key: `audit.${issue.key}` }));

    return (
        // Detail is folded away until the card is hovered (or focused), so the
        // rail keeps its height while the score is still readable at a glance.
        <div className="group rounded-[20px] border border-[#ECE8E4] bg-white p-4 shadow-[0_1px_2px_rgba(28,24,21,0.04)] transition-shadow hover:shadow-[0_4px_16px_rgba(28,24,21,0.08)] dark:border-zinc-700 dark:bg-zinc-900">
            <div className="flex items-center gap-2.5">
                <span className="text-[13.5px] font-bold text-[#1C1815] dark:text-zinc-100">{t('qrCode.scoreTitle')}</span>
                <span className="text-[20px] font-bold leading-none text-[#1C1815] dark:text-zinc-100">{toBnDigits(audit.score)}</span>
                <span className="text-[11.5px] text-[#6B635B]">/ {toBnDigits(100)}</span>
                <span className={`ml-auto rounded-full px-2.5 py-1 text-[11.5px] font-semibold ${GRADE_STYLES[grade]}`}>
                    {t(`qrCode.grades.${grade}`)}
                </span>
            </div>

            <div className="mt-2.5 h-1.5 overflow-hidden rounded-md bg-[#F2EFEC] dark:bg-zinc-800">
                <div
                    className="h-full rounded-md transition-[width] duration-300"
                    style={{ width: `${audit.score}%`, background: barColor(audit.score) }}
                />
            </div>

            <div className="grid grid-rows-[0fr] transition-[grid-template-rows] duration-300 ease-out group-hover:grid-rows-[1fr] group-focus-within:grid-rows-[1fr]">
                <div className="overflow-hidden">
                    <div className="flex flex-col gap-2 pt-2.5">
                        {rows.map((row) => (
                            <div
                                key={row.key}
                                className={`flex gap-2 rounded-[11px] px-3 py-2 text-[12.5px] leading-relaxed ${ISSUE_STYLES[row.level]}`}
                            >
                                {row.busy ? (
                                    <span className="mt-1 h-3 w-3 flex-none animate-spin rounded-full border-2 border-current border-t-transparent" />
                                ) : (
                                    <span className="flex-none font-bold">{ISSUE_MARKS[row.level]}</span>
                                )}
                                <span>{t(`qrCode.${row.key}`)}</span>
                            </div>
                        ))}
                        <div className="text-right font-mono text-[11px] text-[#6B635B]">
                            {t('qrCode.contrast')} {audit.contrast.toFixed(1)}:1
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
